import React, {
  useState,
  useEffect,
  useMemo,
} from "react";

import {
  Upload,
  FileText,
  Trash2,
  Search,
  ShieldCheck,
  Download,
  Eye,
  FolderOpen,
  Plus,
} from "lucide-react";

import { toast } from "react-toastify";

import "../styles/Appendix.css";

function Appendix() {
  const [forms, setForms] =
    useState([]);

  const [selectedFile, setSelectedFile] =
    useState(null);

  const [formName, setFormName] =
    useState("");

  const [formType, setFormType] =
    useState("");

  const [searchTerm, setSearchTerm] =
    useState("");

  const [uploading, setUploading] =
    useState(false);

  const [successUpload, setSuccessUpload] =
    useState(false);

  const API_BASE =
    "https://client-management-backend-8x91.onrender.com";

  useEffect(() => {
    fetchForms();
  }, []);

  const fetchForms = async () => {
    try {
      const res = await fetch(
        `${API_BASE}/forms`
      );

      if (!res.ok)
        throw new Error(
          "Failed to fetch forms"
        );

      const data = await res.json();

      setForms(data);
    } catch (err) {
      console.error(err);

      toast.error(
        "Failed to fetch forms"
      );
    }
  };

  const filteredForms = useMemo(() => {
    return forms
      .filter(
        (form) =>
          form.form_name
            ?.toLowerCase()
            .includes(
              searchTerm.toLowerCase()
            ) ||
          form.form_type
            ?.toLowerCase()
            .includes(
              searchTerm.toLowerCase()
            )
      )
      .sort((a, b) =>
        a.form_name.localeCompare(
          b.form_name
        )
      );
  }, [forms, searchTerm]);

  const displayedForms =
    searchTerm.trim()
      ? filteredForms
      : filteredForms.slice(0, 10);

  const handleUpload = async (e) => {
    e.preventDefault();

    if (
      !formName ||
      !selectedFile
    ) {
      toast.error(
        "Please complete required fields"
      );

      return;
    }

    setUploading(true);

    const formData =
      new FormData();

    formData.append(
      "formName",
      formName
    );

    formData.append(
      "formType",
      formType
    );

    formData.append(
      "file",
      selectedFile
    );

    try {
      await new Promise(
        (resolve) =>
          setTimeout(
            resolve,
            3000
          )
      );

      const res = await fetch(
        `${API_BASE}/forms/upload`,
        {
          method: "POST",
          body: formData,
        }
      );

      if (!res.ok) {
        const errorText =
          await res.text();

        throw new Error(
          errorText
        );
      }

      setSuccessUpload(true);

      fetchForms();

      setFormName("");

      setFormType("");

      setSelectedFile(null);

      setTimeout(() => {
        setUploading(false);

        setSuccessUpload(false);
      }, 2500);
    } catch (err) {
      console.error(err);

      setUploading(false);

      toast.error(
        "Failed to upload form"
      );
    }
  };

  const handleDelete =
    async (id) => {
      const confirmDelete =
        window.confirm(
          "Delete this form?"
        );

      if (!confirmDelete)
        return;

      try {
        const res = await fetch(
          `${API_BASE}/forms/${id}`,
          {
            method: "DELETE",
          }
        );

        if (!res.ok)
          throw new Error(
            "Delete failed"
          );

        toast.success(
          "Form deleted"
        );

        fetchForms();
      } catch (err) {
        console.error(err);

        toast.error(
          "Failed to delete form"
        );
      }
    };

  return (
    <div className="modern-appendix-page">

      {/* HERO */}

      <div className="modern-appendix-hero">

        <div className="modern-appendix-left">

          <div className="modern-appendix-pill">
            <ShieldCheck size={16} />
            Insurance Appendix
          </div>

          <h1 className="rcs-hero-heading">
            Insurance
            Appendix.
          </h1>

          <p className="rcs-hero-description">
            Store, manage and
            organize insurance
            forms, documents,
            templates and
            downloadable policy
            resources.
          </p>
        </div>

        {/* STATS */}

        <div className="modern-appendix-stats-grid">

          <div className="modern-appendix-stat-card">
            <div className="appendix-stat-icon teal">
              <FolderOpen size={22} />
            </div>

            <div>
              <span>
                Total Forms
              </span>

              <h2>
                {
                  forms.length
                }
              </h2>
            </div>
          </div>

          <div className="modern-appendix-stat-card">
            <div className="appendix-stat-icon blue">
              <FileText size={22} />
            </div>

            <div>
              <span>
                Displayed
              </span>

              <h2>
                {
                  displayedForms.length
                }
              </h2>
            </div>
          </div>
        </div>
      </div>

      {/* UPLOAD PANEL */}

      <div className="modern-appendix-upload-panel">

        {!uploading &&
          !successUpload && (
            <form
              className="modern-appendix-upload-form"
              onSubmit={
                handleUpload
              }
            >

              <div className="modern-appendix-upload-grid">

                <div className="modern-appendix-group">
                  <label>
                    Form Name
                  </label>

                  <input
                    type="text"
                    placeholder="Motor Policy Form"
                    value={
                      formName
                    }
                    onChange={(
                      e
                    ) =>
                      setFormName(
                        e.target
                          .value
                      )
                    }
                    required
                  />
                </div>

                <div className="modern-appendix-group">
                  <label>
                    Form Type
                  </label>

                  <input
                    type="text"
                    placeholder="Optional category"
                    value={
                      formType
                    }
                    onChange={(
                      e
                    ) =>
                      setFormType(
                        e.target
                          .value
                      )
                    }
                  />
                </div>

                <div className="modern-appendix-group">
                  <label>
                    Upload PDF
                  </label>

                  <input
                    type="file"
                    accept=".pdf"
                    onChange={(
                      e
                    ) =>
                      setSelectedFile(
                        e.target
                          .files[0]
                      )
                    }
                    required
                  />
                </div>
              </div>

              <button
                type="submit"
                className="modern-appendix-upload-btn"
              >
                <Upload size={18} />
                Upload Form
              </button>
            </form>
          )}

        {/* LOADER */}

        {uploading &&
          !successUpload && (
            <div className="modern-appendix-loader-screen">

              <div className="modern-appendix-loader">

                <div className="modern-appendix-loader-ring" />

                <div className="modern-appendix-loader-ring-two" />

                <div className="modern-appendix-loader-core">
                  RCS
                </div>
              </div>

              <h2>
                Uploading Form
              </h2>

              <p>
                Processing and
                securing insurance
                document...
              </p>
            </div>
          )}

        {/* SUCCESS */}

        {successUpload && (
          <div className="modern-appendix-success-screen">

            <div className="modern-appendix-success-tick">
              ✓
            </div>

            <h2>
              Upload Complete
            </h2>

            <p>
              Insurance form was
              successfully added
              to the appendix.
            </p>
          </div>
        )}
      </div>

      {/* TOOLBAR */}

      <div className="modern-appendix-toolbar">

        <div className="modern-appendix-search">
          <Search size={18} />

          <input
            type="text"
            placeholder="Search forms or categories..."
            value={searchTerm}
            onChange={(e) =>
              setSearchTerm(
                e.target.value
              )
            }
          />
        </div>

        <div className="modern-appendix-results">
          {searchTerm.trim()
            ? `${filteredForms.length} form(s) found`
            : `Showing top 10 forms`}
        </div>
      </div>

      {/* TABLE */}

      <div className="modern-appendix-table-wrapper">

        <table className="modern-appendix-table">

          <thead>
            <tr>
              <th>
                Form Name
              </th>
              <th>
                Category
              </th>
              <th>
                Uploaded
              </th>
              <th>
                Document
              </th>
              <th>
                Actions
              </th>
            </tr>
          </thead>

          <tbody>

            {displayedForms.length >
            0 ? (
              displayedForms.map(
                (form) => (
                  <tr
                    key={
                      form.id
                    }
                  >

                    <td>
                      <div className="appendix-name-cell">

                        <div className="appendix-avatar">
                          <FileText
                            size={
                              18
                            }
                          />
                        </div>

                        <div>
                          <h4>
                            {
                              form.form_name
                            }
                          </h4>

                          <span>
                            Insurance
                            Form
                          </span>
                        </div>
                      </div>
                    </td>

                    <td>
                      <span className="appendix-category-pill">
                        {form.form_type ||
                          "General"}
                      </span>
                    </td>

                    <td>
                      {form.uploaded_at
                        ? new Date(
                            form.uploaded_at
                          ).toLocaleDateString()
                        : "—"}
                    </td>

                    <td>
                      <a
                        href={
                          form.file_url
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                        className="appendix-view-btn"
                      >
                        <Eye
                          size={
                            16
                          }
                        />
                        View
                      </a>
                    </td>

                    <td>

                      <button
                        className="appendix-delete-btn"
                        onClick={() =>
                          handleDelete(
                            form.id
                          )
                        }
                      >
                        <Trash2
                          size={
                            16
                          }
                        />
                      </button>

                    </td>
                  </tr>
                )
              )
            ) : (
              <tr>

                <td
                  colSpan="5"
                  className="modern-appendix-empty"
                >

                  <FolderOpen
                    size={46}
                  />

                  <h3>
                    No Forms Found
                  </h3>

                  <p>
                    No insurance
                    forms currently
                    available.
                  </p>

                </td>

              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default Appendix;